import { ACOES_AVALIACAO, ETAPAS_AVALIACAO_ORDENADAS, SLOTS_MONITOR, leituraCurta } from './catalogoAcoes'
import { achadoAtualDaVitima, estagiosDaVitima, obterAchadoAtual, ultimaLeitura } from './efeitosClinicos'
import type { DadoClinicoAcao, EstadoVitimaNaSessao, EtapaAvaliacao, FichaVitimaAtendimento, IdAcaoAvaliacao } from './tipos'

/**
 * Visão de leitura de uma ficha, no formato do simulador de treinamento:
 * bloco do ator, XABCDE rápido (inicial × estágios), avaliação secundária e
 * passagem de caso. É só projeção da ficha: não decide gravidade nem sabe o
 * que o aluno já examinou.
 */

/** O que a vítima apresenta em um estágio, pronto para uma célula de tabela. */
export interface CelulaFicha {
  readonly texto: string
  readonly estado: DadoClinicoAcao['estado']
  /** o texto é diferente do estágio anterior (a primeira coluna nunca "mudou") */
  readonly mudou: boolean
  readonly nota?: string
}

export interface LinhaFicha {
  readonly idAcao: IdAcaoAvaliacao
  readonly rotulo: string
  /** uma célula por coluna de `FichaClinicaMontada.estagios`, na mesma ordem */
  readonly celulas: readonly CelulaFicha[]
}

export interface GrupoFicha {
  readonly etapa: EtapaAvaliacao
  readonly sigla: string
  readonly rotulo: string
  readonly linhas: readonly LinhaFicha[]
}

export interface LinhaPassagem {
  readonly parametro: string
  readonly inicial: string
  /** vazio quando a ficha não tem evolução */
  readonly tardia: string
}

export interface FichaClinicaMontada {
  readonly cabecalho: { readonly titulo: string; readonly papel: string; readonly mecanismo: string }
  readonly ator: {
    readonly descricao: string
    readonly condicao: string
    readonly aparencia: string
    readonly instrucoes: string
  }
  /** colunas das tabelas: sempre "Inicial" primeiro, depois os estágios da ficha */
  readonly estagios: readonly { readonly id: string; readonly rotulo: string }[]
  readonly inicial: readonly GrupoFicha[]
  readonly secundaria: readonly GrupoFicha[]
  readonly passagem: {
    readonly linhas: readonly LinhaPassagem[]
    readonly tratamentos: readonly string[]
  }
}

export const TEXTO_SEM_DADO = '—'

const ETAPAS_INICIAIS: readonly EtapaAvaliacao[] = ['X', 'A', 'B', 'C', 'D', 'E']
const ETAPAS_SECUNDARIAS: readonly EtapaAvaliacao[] = ['secundaria', 'sinais', 'sample']

const ROTULO_ETAPA_SECUNDARIA: Readonly<Partial<Record<EtapaAvaliacao, string>>> = {
  secundaria: 'Cabeça aos pés',
  sinais: 'Sinais complementares',
  sample: 'Histórico (SAMPLE)',
}

function textoDoAchado(achado: DadoClinicoAcao): string {
  return achado.estado === 'nao_informado' ? TEXTO_SEM_DADO : achado.valor
}

function montarLinha(
  ficha: FichaVitimaAtendimento,
  idAcao: IdAcaoAvaliacao,
  rotulo: string,
  idsEstagios: readonly string[],
): LinhaFicha {
  let anterior: string | null = null
  const celulas = idsEstagios.map((idEstagio): CelulaFicha => {
    const achado = obterAchadoAtual(ficha, idAcao, idEstagio)
    const texto = textoDoAchado(achado)
    const mudou = anterior !== null && texto !== anterior
    anterior = texto
    return { texto, estado: achado.estado, mudou, ...(achado.notaInstrutor ? { nota: achado.notaInstrutor } : {}) }
  })
  return { idAcao, rotulo, celulas }
}

function montarGrupos(
  ficha: FichaVitimaAtendimento,
  etapas: readonly EtapaAvaliacao[],
  idsEstagios: readonly string[],
  rotuloDoGrupo: (etapa: EtapaAvaliacao, rotuloCatalogo: string) => string,
): GrupoFicha[] {
  return etapas.map((etapa) => {
    const def = ETAPAS_AVALIACAO_ORDENADAS.find((e) => e.id === etapa)!
    return {
      etapa,
      sigla: etapa.length === 1 ? etapa : '',
      rotulo: rotuloDoGrupo(etapa, def.rotulo),
      linhas: ACOES_AVALIACAO.filter((a) => a.etapa === etapa).map((a) => montarLinha(ficha, a.id, a.rotulo, idsEstagios)),
    }
  })
}

function linhasDaPassagem(ficha: FichaVitimaAtendimento, idFinal: string | null): LinhaPassagem[] {
  const leitura = (idAcao: IdAcaoAvaliacao, idEstagio: string): string => {
    const achado = obterAchadoAtual(ficha, idAcao, idEstagio)
    return achado.estado === 'nao_informado' ? TEXTO_SEM_DADO : leituraCurta(achado)
  }
  const sinais = SLOTS_MONITOR.map((slot) => ({
    parametro: slot.sigla,
    inicial: leitura(slot.idAcao, 'inicial'),
    tardia: idFinal ? leitura(slot.idAcao, idFinal) : '',
  }))
  return [
    { parametro: 'Mecanismo', inicial: ficha.perfil.mecanismo, tardia: '' },
    { parametro: 'Lesões identificadas', inicial: ficha.perfil.condicaoDescrita, tardia: '' },
    ...sinais,
  ]
}

export function montarFichaClinica(ficha: FichaVitimaAtendimento): FichaClinicaMontada {
  const estagios = estagiosDaVitima(ficha)
  const idsEstagios = estagios.map((e) => e.id)
  const idFinal = idsEstagios.length > 1 ? idsEstagios[idsEstagios.length - 1]! : null
  const { perfil } = ficha
  return {
    cabecalho: { titulo: ficha.titulo, papel: perfil.papel, mecanismo: perfil.mecanismo },
    ator: {
      descricao: perfil.descricao ?? `${perfil.papel}. ${perfil.mecanismo}`,
      condicao: perfil.condicaoDescrita,
      aparencia: perfil.aparencia,
      instrucoes: perfil.ator,
    },
    estagios,
    inicial: montarGrupos(ficha, ETAPAS_INICIAIS, idsEstagios, (_, rotulo) => rotulo),
    secundaria: montarGrupos(ficha, ETAPAS_SECUNDARIAS, idsEstagios, (etapa, rotulo) => ROTULO_ETAPA_SECUNDARIA[etapa] ?? rotulo),
    passagem: { linhas: linhasDaPassagem(ficha, idFinal), tratamentos: ficha.intervencoesCitadas },
  }
}

/** O que a equipe já sabe de cada exame, frente ao que a vítima apresenta agora. */
export interface SituacaoVerificacao {
  /** a última leitura ainda vale: a vítima segue apresentando o que foi registrado */
  readonly verificados: ReadonlySet<IdAcaoAvaliacao>
  /** houve leitura, mas a vítima mudou desde então: falta reavaliar */
  readonly desatualizados: ReadonlySet<IdAcaoAvaliacao>
  /** o que a equipe leu por último, por exame (vale mesmo quando desatualizado) */
  readonly leituras: ReadonlyMap<IdAcaoAvaliacao, DadoClinicoAcao>
}

const mesmoAchado = (a: DadoClinicoAcao, b: DadoClinicoAcao): boolean =>
  a.valor === b.valor && a.leitura === b.leitura && a.estado === b.estado

/**
 * "Verificado" é verificado no estado atual da vítima: se a evolução mudou o
 * valor de um exame já lido, ele volta a pedir reavaliação (e a leitura
 * anterior continua guardada). Se o valor não mudou, a leitura segue valendo.
 */
export function situacaoDaVerificacao(ficha: FichaVitimaAtendimento, estado: EstadoVitimaNaSessao): SituacaoVerificacao {
  const verificados = new Set<IdAcaoAvaliacao>()
  const desatualizados = new Set<IdAcaoAvaliacao>()
  const leituras = new Map<IdAcaoAvaliacao, DadoClinicoAcao>()
  for (const idAcao of estado.acoesReveladas) {
    const ultima = ultimaLeitura(estado, idAcao)
    if (!ultima) continue
    leituras.set(idAcao, ultima.achado)
    if (mesmoAchado(ultima.achado, achadoAtualDaVitima(ficha, idAcao, estado))) verificados.add(idAcao)
    else desatualizados.add(idAcao)
  }
  return { verificados, desatualizados, leituras }
}
